import React from 'react'
import dayjs from 'dayjs'
import { Eye, MoreCircle } from 'iconsax-react'
import { Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { sortFieldJobVacanciesEnum } from '../hooks/jobVacancyEnum'
import IconSortAscDesc from 'components/tableHelpers/IconSortAscDesc'
import {
  AdminsJobBoardsVacanciesGetJobVacancyListResponseItem,
  AdminsJobBoardsVacanciesGetJobVacancyListResponseItemCategory,
  AdminsJobBoardsVacanciesGetJobVacancyListResponseItemLocation,
  OrderDirectionEnum,
} from 'generated/api'

import jobVacancyStatusBadgeVariant from '../hooks/jobVacancyStatusBadgeVariant'
import { mapJobVacancyCategory, mapJobVacancyStatus } from 'helpers/wording'
import Badge from 'components/badge/Badge'

interface IJobVacancyTableProps {
  data: AdminsJobBoardsVacanciesGetJobVacancyListResponseItem[]
  loading: boolean
  columnsSortBy: OrderDirectionEnum
  columnsSortField: sortFieldJobVacanciesEnum
  onSort: (field: sortFieldJobVacanciesEnum) => void
  onClickDetail: (id: string) => void
}

export default function JobVacancyTable(props: IJobVacancyTableProps) {
  const columns: ColumnsType<AdminsJobBoardsVacanciesGetJobVacancyListResponseItem> = [
    {
      width: '5%',
      dataIndex: 'id',
      key: 'id',
      title: (
        <div className="flex">
          <Typography.Text className="text-sm font-semibold text-[#333539]">No.</Typography.Text>
        </div>
      ),
      render: (_text, _row, index) => (
        <div className="flex flex-col">
          <Typography.Text className="text-sm font-normal text-[#333539]">
            {index + 1}.
          </Typography.Text>
        </div>
      ),
    },
    {
      width: '10%',
      dataIndex: 'category',
      key: 'category',
      title: (
        <div
          data-testid="table_head_category"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539]">
            Kategori
          </Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobVacanciesEnum.CATEGORY}
            columnSortBy={props.columnsSortBy}
            columnSortField={props.columnsSortField}
          />
        </div>
      ),
      render: (category: AdminsJobBoardsVacanciesGetJobVacancyListResponseItemCategory) => (
        <div
          data-testid="table_body_category"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>{mapJobVacancyCategory(category)}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => props.onSort(sortFieldJobVacanciesEnum.CATEGORY),
        }
      },
    },
    {
      width: '25%',
      dataIndex: 'jobName',
      key: 'jobName',
      title: (
        <div
          data-testid="table_head_jobName"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539]">
            Nama Pekerjaan
          </Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobVacanciesEnum.JOBNAME}
            columnSortBy={props.columnsSortBy}
            columnSortField={props.columnsSortField}
          />
        </div>
      ),
      render: jobName => (
        <div
          data-testid="table_body_jobName"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>{jobName}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => props.onSort(sortFieldJobVacanciesEnum.JOBNAME),
        }
      },
    },
    {
      width: '20%',
      dataIndex: 'location',
      key: 'location',
      title: (
        <div
          data-testid="table_head_location"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539]">Lokasi</Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobVacanciesEnum.LOCATION}
            columnSortBy={props.columnsSortBy}
            columnSortField={props.columnsSortField}
          />
        </div>
      ),
      render: (location: AdminsJobBoardsVacanciesGetJobVacancyListResponseItemLocation) => (
        <div
          data-testid="table_body_location"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>{location.areaName}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => props.onSort(sortFieldJobVacanciesEnum.LOCATION),
        }
      },
    },
    {
      width: '15%',
      dataIndex: 'createdAtUtc0',
      key: 'createdAtUtc0',
      title: (
        <div
          data-testid="table_head_createdAtUtc0"
          className="flex justify-between items-center hover:cursor-pointer"
        >
          <Typography.Text className="text-sm font-semibold text-[#333539]">
            Tanggal Dibuat
          </Typography.Text>
          <IconSortAscDesc
            columnName={sortFieldJobVacanciesEnum.CREATEDATUTC0}
            columnSortBy={props.columnsSortBy}
            columnSortField={props.columnsSortField}
          />
        </div>
      ),
      render: (createdAt: number) => (
        <div
          data-testid="table_body_createdAtUtc0"
          className="flex flex-col text-sm font-normal text-[#333539]"
        >
          <Typography.Text>{dayjs(createdAt).format('DD MMMM YYYY')}</Typography.Text>
        </div>
      ),
      onHeaderCell: () => {
        return {
          onClick: () => props.onSort(sortFieldJobVacanciesEnum.CREATEDATUTC0),
        }
      },
    },
    {
      width: '10%',
      dataIndex: 'totalApplicant',
      key: 'totalApplicant',
      title: (
        <div className="flex">
          <Typography.Text className="text-sm font-semibold text-[#333539]">
            Pelamar
          </Typography.Text>
        </div>
      ),
      render: (totalApplicant: number) => (
        <div className="flex flex-col">
          <Typography.Text className="text-sm font-normal text-[#333539]">
            {totalApplicant}
          </Typography.Text>
        </div>
      ),
    },
    {
      width: '15%',
      dataIndex: 'status',
      key: 'status',
      title: (
        <div className="flex gap-2 justify-between">
          <Typography.Text className="text-sm font-semibold text-[#333539] text-start">
            Status
          </Typography.Text>
          <MoreCircle size="16" color="#333539" className="self-center" />
        </div>
      ),
      render: (status, item, index) => (
        <div className="flex gap-2 justify-between">
          <Badge
            title={mapJobVacancyStatus(status)}
            variant={jobVacancyStatusBadgeVariant(status)}
          />
          <Eye
            data-testid={`table_body_detail_${index}`}
            size="16"
            color="#757575"
            className="self-center hover:cursor-pointer"
            onClick={() => props.onClickDetail(item.id)}
          />
        </div>
      ),
    },
  ]

  return (
    <Table
      rowKey="id"
      loading={props.loading}
      data-testid="table_job_vacancy"
      className="table-primary table-primary--admin"
      columns={columns}
      dataSource={props.data}
      pagination={false}
    />
  )
}
